import { useTranslation } from "react-i18next"

import "./SeatPicker.css"

/* Whose eyes the view is using — the one control that turns a board somebody is watching into a
   game somebody can read.

   Both watchers need it and they need the same one (owner, 2026-09-04): a replay and a live
   spectator are the same problem a round apart. Without a seat, every panel under the map has no
   player to answer for — the roster is empty, the city view has no garrison to show, the resource
   chips have nothing to count, and the events belong to nobody — so what is left is coloured
   squares. Pick a seat and all of it answers for that player — through their fog for a live
   spectator (the "Spectator" option is the whole board), and as the replay's fog eye says in a
   replay.

   A drop-down, not a strip of chips (owner, 2026-09-06). Four chips in the game's top bar ran
   into the resource chips beside them and the strip's cap turned the seats nobody could see into
   a scroll nobody knew was there; a select is one seat wide however many there are, and the list
   opens over the bar instead of pushing it.

   `seats` are player rows in either spelling: the replay hands over the raw payload's seats
   (`is_bot`), the game view hands over the store's (`user.isBot`). Reading both here is one
   `??` against making one of the two callers reshape a list purely to be handed straight back. */
const seatId = (seat) => seat.id ?? seat.user?.id

/* `everything` is the live spectator's first option (owner, 2026-09-29: "put a 'player' like
   'Spectator' that sees everything, and leave the 'VIENDO COMO' the mode where we only see what
   the other player sees"): `{ on, onPick }`, where `on` means the whole board is showing and a
   seat means that seat's fog. The replay passes none — its fog eye is its own switch. The option
   value can never be a seat's: ids are numbers. */
const EVERYTHING = "everything"

const SeatPicker = ({ seats = [], value, onChange, everything = null, className = "" }) => {
  const { t } = useTranslation()
  if (seats.length === 0) return null

  const seesAll = Boolean(everything?.on)
  const chosen = seesAll ? null : seats.find((seat) => seatId(seat) === value)
  return (
    <label className={`seat-picker ${className}`.trim()}>
      <span className="seat-picker-label">{t("replay.watchAs")}</span>
      <select
        className={`seat-select ${chosen?.team ?? ""}`.trim()}
        aria-label={t("replay.watchAs")}
        value={seesAll ? EVERYTHING : value == null ? "" : String(value)}
        onChange={(event) => {
          if (everything && event.target.value === EVERYTHING) {
            everything.onPick()
            return
          }
          // Option values are strings; the ids the store keys on are not.
          const seat = seats.find((candidate) => String(seatId(candidate)) === event.target.value)
          if (seat) onChange(seatId(seat))
        }}
      >
        {everything && <option value={EVERYTHING}>{t("replay.watchEverything")}</option>}
        {seats.map((seat) => {
          const id = seatId(seat)
          const username = seat.username ?? seat.user?.username
          const isBot = seat.is_bot ?? seat.user?.isBot
          return (
            <option key={id} value={String(id)}>
              {username}
              {isBot ? " 🤖" : ""}
            </option>
          )
        })}
      </select>
    </label>
  )
}

export default SeatPicker
